import { assign } from 'xstate';

import { getActiveModificationsCount } from '../../helpers';
import {
  type CustomizationMachineAction,
  type EncouragedChangeEntry,
  type ExtendedModifierGroup,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

export const generateEncouragedChangesList: CustomizationMachineAction = assign((args) => {
  const { context } = args;

  const { modificationGroups, modifications, product } = context;

  const activeModifications = modifications?.active;
  const shouldSkip = !activeModifications || !modificationGroups;

  if (shouldSkip) {
    return { encouragedChangesList: [] };
  }

  const modificationsCount = getActiveModificationsCount({ activeModifications }); // prettier-ignore
  const originalModifierGroupMaxes = createModifierGroupMaxes(product?.modifierGroups);
  const createEncouragementChangeEntry = createEncouragementChangeEntryFactory(
    modificationsCount,
    originalModifierGroupMaxes,
  );

  const encouragedChangesList = modificationGroups
    .map(createEncouragementChangeEntry)
    .filter(checkIfValidEntry);

  return { encouragedChangesList };
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function createEncouragementChangeEntryFactory(
  modificationsCount: Record<string, number>,
  originalModifierGroupMaxes: Map<string, number>,
) {
  return (group: ExtendedModifierGroup): EncouragedChangeEntry | null => {
    const currentSelection = modificationsCount[group.name] ?? 0;
    const maxAggregateQuantity =
      originalModifierGroupMaxes.get(group.id) ?? group.maxAggregateQuantity;

    const shouldEncourageAboveMinSelection = checkIfShouldEncourageAboveMinSelection(group, currentSelection, maxAggregateQuantity); // prettier-ignore
    const shouldEncourageMaxSelection = checkIfShouldEncourageMaxSelection(group, currentSelection, maxAggregateQuantity); // prettier-ignore

    // ─────────────────────────────────────────────────────────────────────

    if (shouldEncourageAboveMinSelection) {
      return createAboveMinSelectionEntry(group, currentSelection, maxAggregateQuantity);
    }

    if (shouldEncourageMaxSelection) {
      return createMaxSelectionEntry(group, currentSelection, maxAggregateQuantity);
    }

    // ─────────────────────────────────────────────────────────────────────

    return null;
  };
}

function checkIfShouldEncourageAboveMinSelection(
  group: ExtendedModifierGroup,
  currentSelection: number,
  maxAggregateQuantity: number,
): boolean {
  const { encourageAboveMinSelection, minAggregateQuantity } = group;

  if (Number.isNaN(currentSelection)) {
    return false;
  }

  return (
    encourageAboveMinSelection &&
    maxAggregateQuantity > 0 &&
    currentSelection <= minAggregateQuantity
  );
}

function checkIfShouldEncourageMaxSelection(
  group: ExtendedModifierGroup,
  currentSelection: number,
  maxAggregateQuantity: number,
): boolean {
  const { encourageMaxSelection } = group;

  if (Number.isNaN(currentSelection)) {
    return false;
  }

  return (
    encourageMaxSelection && maxAggregateQuantity > 0 && currentSelection < maxAggregateQuantity
  );
}

function createAboveMinSelectionEntry(
  group: ExtendedModifierGroup,
  currentSelection: number,
  maxAggregateQuantity: number,
): EncouragedChangeEntry {
  return {
    groupDisplayName: group.displayName,
    currentSelection,
    encouragedMinSelection: group.minAggregateQuantity,
    encouragedMaxSelection: maxAggregateQuantity,
    modificationIngredientKind: group.modificationIngredientKind,
    encouragementVariation: 'above-min-selection',
  };
}

function createMaxSelectionEntry(
  group: ExtendedModifierGroup,
  currentSelection: number,
  maxAggregateQuantity: number,
): EncouragedChangeEntry {
  return {
    groupDisplayName: group.displayName,
    currentSelection,
    encouragedMinSelection: group.minAggregateQuantity,
    encouragedMaxSelection: maxAggregateQuantity,
    modificationIngredientKind: group.modificationIngredientKind,
    encouragementVariation: 'max-selection',
  };
}

function checkIfValidEntry<T>(item: T | null): item is T {
  return item !== null;
}

function createModifierGroupMaxes(
  groups: ReadonlyArray<Pick<ExtendedModifierGroup, 'id' | 'maxAggregateQuantity'>> | undefined,
) {
  return new Map(groups?.map((group) => [group.id, group.maxAggregateQuantity]) ?? []);
}
